import styled from 'styled-components';
import { z } from 'zod';
import {
  ButtonContainer,
  ErrorWrapper,
  InputCheck,
  InputWrapper,
  Loading,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { APIEndPoints, Request } from '../../apis';
import { Logger } from '../../common';
import { toast } from 'react-hot-toast';

export type TUserCreationType = 'platformUser' | 'tenant';

type TNewUserMerchantModal = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  merchantId: string;
  refreshPage: () => void;
  hideRole?: boolean;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

type UserPermissions = {
  readOnlyAccess: boolean;
  paymentAssistAgreement: boolean;
};

type EmailPreferences = {
  enableAllNotifications: boolean;
};

type TCreateUserRequest = {
  merchantId: string;
  email: string;
  mobile: string;
  name: string;
  title: string;
  temporaryPassword: string;
  permissions: UserPermissions;
  emailPreferences: EmailPreferences;
  role: string;
};

const newUserFormSchema = z.object({
  fullName: z
    .string()
    .regex(
      /^[a-zA-Z]{1,}( {1,2}[a-zA-Z]{1,}){0,}$/,
      'Please enter a valid full name.'
    ),
  title: z.string().min(1, 'Title must be at least 1 character.'),
  email: z.string().email('Please enter a valid email address.'),
  mobile: z.string().regex(/^04\d{8}$/, 'Please enter a valid mobile.'),
  temporaryPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+~`|}{[\]\\:;?><,./-=])[A-Za-z0-9!@#$%^&*()_+~`|}{[\]\\:;?><,./-=]{8,}$/,
      'Temporary password must include one uppercase and lowercase letter, number, special character and be at least 8 characters long.'
    ),
  merchantName: z.string().optional(),
  role: z.string().optional(),
  pas: z
    .enum(
      ['Allowed to view and download', 'Not Allowed to view and download'],
      {
        invalid_type_error:
          'Please enter a selection for Payment Assist agreement.',
        required_error: 'Please enter a selection for Payment Assist agreement.'
      }
    )
    .optional()
});

type NewUserFormScheme = z.infer<typeof newUserFormSchema>;

const NewUserMerchantModal = ({
  isModalOpen,
  handleModalClose,
  merchantId,
  refreshPage,
  hideRole
}: TNewUserMerchantModal) => {
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<NewUserFormScheme>({
    resolver: zodResolver(newUserFormSchema)
  });

  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [roleErrorMessage, setRoleErrorMessage] = useState('');

  const onSubmit: SubmitHandler<NewUserFormScheme> = async (data: any) => {
    setErrorMessage('');
    setRoleErrorMessage('');

    const { email, fullName, mobile, pas, temporaryPassword, title, role } =
      data;

    if (!hideRole) {
      if (!['Manager', 'Sales'].includes(role)) {
        setRoleErrorMessage('Please select a valid role.');
        return;
      }
    }

    try {
      let apiEndpoint = null;

      if (!hideRole && role === 'Manager') {
        apiEndpoint = `${APIEndPoints.usersMerchantAdmin}/${merchantId}`;
      } else if ((!hideRole && role === 'Sales') || hideRole) {
        apiEndpoint = `${APIEndPoints.usersMerchant}/${merchantId}`;
      } else {
        setErrorMessage('Something unexpected occurred.');
        return;
      }

      const createUserRequest: Partial<TCreateUserRequest> = {
        emailPreferences: {
          enableAllNotifications: false // setting as false since we're hiding this checkbox for now and will be easy to add back in later rather than removing
        },
        temporaryPassword: temporaryPassword,
        title: title,
        name: fullName,
        mobile: mobile,
        email: email
      };

      if (merchantId) {
        createUserRequest.merchantId = merchantId;
      }

      createUserRequest.permissions = {
        paymentAssistAgreement: pas === 'Allowed to view and download',
        readOnlyAccess: false // setting as false since we're hiding this checkbox for now and will be easy to add back in later rather than removing
      };
      createUserRequest.role = hideRole ? 'Sales' : role;

      setCreating(true);
      await Request.post(apiEndpoint, z.any(), createUserRequest);
      Logger.info({ source: 'NewUserMerchantModal', message: 'User created' });
      setCreating(false);
      toast.success('User created.');
      refreshPage();
      reset();
      handleModalClose();
    } catch (err: any) {
      setErrorMessage(err.message);
      setCreating(false);
    }
  };
  // z0Bp2RzY3r61t!#v
  return (
    <Modal
      handleClose={handleModalClose}
      isModalVisible={isModalOpen}
      content={
        <>
          <SectionPreTitle>User</SectionPreTitle>
          <SectionTitle>Create New User</SectionTitle>
          <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
            <SectionWrapper>
              <SectionHeading>Login Details</SectionHeading>
              <InputWrapper
                name="fullName"
                type="text"
                label="Full Name"
                required
                register={register}
                errorMessage={errors.fullName?.message}
              />
              <InputWrapper
                name="title"
                type="text"
                label="Title"
                required
                register={register}
                errorMessage={errors.title?.message}
              />
              <InputWrapper
                name="email"
                type="email"
                label="Email"
                required
                autoFill={false}
                register={register}
                errorMessage={errors.email?.message}
              />
              <InputWrapper
                name="mobile"
                type="text"
                label="Mobile"
                required
                register={register}
                errorMessage={errors.mobile?.message}
              />
              <InputWrapper
                name="temporaryPassword"
                type="password"
                label="Temporary Password"
                required
                autoFill={false}
                register={register}
                errorMessage={errors.temporaryPassword?.message}
              />
            </SectionWrapper>
            <SectionWrapper>
              <SectionHeading>Permissions</SectionHeading>
              {!hideRole && (
                <InputWrapper
                  name="role"
                  type="select"
                  label="Role Hierarchy"
                  required
                  options={['Manager', 'Sales']}
                  register={register}
                  errorMessage={roleErrorMessage || errors.role?.message}
                />
              )}

              {/* hiding checkbox for now till implemented */}
              {/* <InputCheck
                name="readOnly"
                label="Read Only Access"
                required
                register={register}
                errorMessage={errors.readOnly?.message}
              />
              */}
              <InputWrapper
                name="pas"
                type="select"
                label="Payment Assist agreement"
                required
                options={[
                  'Allowed to view and download',
                  'Not Allowed to view and download'
                ]}
                register={register}
                errorMessage={errors.pas?.message}
              />
            </SectionWrapper>
            {/* hiding checkbox for now till implemented */}
            {/* <SectionWrapper>
              <SectionHeading>Notifications</SectionHeading>
              <InputCheck
                name="emailNotifications"
                label="Receive Email Notifications"
                required
                register={register}
                errorMessage={errors.emailNotifications?.message}
              />
            </SectionWrapper> */}

            {errorMessage && (
              <div>
                <ErrorWrapper>{errorMessage}</ErrorWrapper>
              </div>
            )}
            <FormFooter
              horizontalSpacing="10px"
              verticalSpacing="0px"
              justify="end"
            >
              {creating ? (
                <Loading />
              ) : (
                <>
                  <SecondaryButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalClose();
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit" disabled={creating}>
                    Create User
                  </PrimaryButton>
                </>
              )}
            </FormFooter>
          </form>
        </>
      }
    />
  );
};

export { NewUserMerchantModal };
